import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDocumentAcknowledgement } from '@/hooks/useDocumentAcknowledgement';
import { LANGUAGE_COOKIE_NAME } from '@/lib/i18n/types';
import { defaultLanguage } from '@/lib/i18n/languageDetection';
import type { SupportedLanguage } from '@/components/multilingual/LanguageSelector';
import { DOCUMENT_VERSIONS } from '@/lib/constants/documentVersions';

/**
 * Localized date formatter for version strings (YYYY-MM-DD).
 */
const formatVersionDate = (version: string, language: SupportedLanguage): string => {
  const locale =
    language === 'de' ? 'de-DE' :
    language === 'fr' ? 'fr-FR' :
    language === 'es' ? 'es-ES' : 'en-US';
  try {
    const date = new Date(version);
    if (isNaN(date.getTime())) return version;
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return version;
  }
};

interface BannerCopy {
  title: string;
  body: string;
  whatChanged: string;
  acknowledge: string;
  dismiss: string;
}

/**
 * Versioned copy map. Outer key = language, inner key = the
 * `DOCUMENT_VERSIONS.privacyPolicy` value the copy applies to.
 *
 * For any version NOT in the map, the banner falls back to a generic message
 * ("Privacy & Cookie Policy was updated on {date}") so future version bumps
 * don't display stale, misleading narratives.
 *
 * When bumping `DOCUMENT_VERSIONS.privacyPolicy` or `.cookiePolicy` for a
 * narrative-worthy reason (new sub-processor, new region, new category), add
 * a fresh entry here keyed by the new version. Otherwise the generic fallback
 * is correct and sufficient.
 */
const VERSIONED_COPY: Record<SupportedLanguage, Record<string, Pick<BannerCopy, 'title' | 'body'>>> = {
  en: {
    '2026-08-15': {
      title: 'Privacy Policy & Terms updated',
      body: 'PaperVerse Writer is now covered: new sub-processors disclosed (Google Cloud Run in the EU/EEA for LaTeX compilation, OpenAlex in the US for literature search — search terms only, no manuscript content), AI processing in the Writer is based on contract performance, and the Terms add digital-product rights (§§ 327 ff. BGB), AI integrity duties and a clarified, revocable licence for private drafts. Statutory references updated from TMG to DDG.',
    },
    '2026-04-18': {
      title: 'Privacy & Cookie Policy updated',
      body: 'We migrated our backend to AWS Ireland. Your data is now stored in the EU/EEA — no third-country transfer for main processing.',
    },
    '2026-04-29': {
      title: 'Privacy Policy & Terms updated',
      body: 'Clarifications to the review-invitation email section (DPF + SCC for Resend, webhook events disclosure, retention wording) and disclosure of the existing 20-invitations-per-30-days author limit in the Terms.',
    },
    '2026-04-30': {
      title: 'Legal pages — editorial cleanup',
      body: 'Internal table names removed, model versions generalised, the contract-language clause now covers German and English (German prevails), the Imprint citation updated to § 18 (2) MStV, and the Terms-change notice aligned to the standard 30 days. No new processing.',
    },
    '2026-05-01': {
      title: 'Privacy Policy & Terms updated',
      body: 'Visibility description updated for the new share feature for private manuscripts, and § 15 broadened to cover both review and share invitations under the same safeguards (3 per manuscript, 14-day expiry, 1-click unsubscribe, suppression list). No new data categories, no new recipients, no new legal bases.',
    },
  },
  de: {
    '2026-08-15': {
      title: 'Datenschutzerklärung & AGB aktualisiert',
      body: 'PaperVerse Writer ist nun abgedeckt: neue Auftragsverarbeiter offengelegt (Google Cloud Run in der EU/EWR für die LaTeX-Kompilierung, OpenAlex in den USA für die Literatursuche — nur Suchbegriffe, keine Manuskriptinhalte), die KI-Verarbeitung im Writer erfolgt zur Vertragserfüllung, und die AGB ergänzen Rechte bei digitalen Produkten (§§ 327 ff. BGB), Pflichten zur wissenschaftlichen Integrität sowie eine klargestellte, widerrufliche Lizenz für private Entwürfe. Gesetzesverweise von TMG auf DDG aktualisiert.',
    },
    '2026-04-18': {
      title: 'Datenschutz- & Cookie-Richtlinie aktualisiert',
      body: 'Wir haben unser Backend auf AWS Irland migriert. Ihre Daten werden nun in der EU/EWR gespeichert — kein Drittlandtransfer für die Hauptverarbeitung.',
    },
    '2026-04-29': {
      title: 'Datenschutzerklärung & AGB aktualisiert',
      body: 'Präzisierungen im Abschnitt zu Review-Einladungs-E-Mails (DPF + Standardvertragsklauseln für Resend, Offenlegung der Webhook-Ereignisse, Speicherdauer-Formulierung) sowie Offenlegung der bereits geltenden Obergrenze von 20 Einladungen je Nutzer in 30 Tagen in den AGB.',
    },
    '2026-04-30': {
      title: 'Rechtsseiten — redaktionelle Bereinigung',
      body: 'Interne Tabellennamen entfernt, Modellversionen verallgemeinert, Vertragssprachen-Klausel deckt nun Deutsch und Englisch ab (deutsche Fassung maßgeblich), Impressum-Zitat auf § 18 Abs. 2 MStV aktualisiert und die AGB-Änderungs­ankündigungs­frist auf marktübliche 30 Tage angepasst. Keine neue Verarbeitung.',
    },
    '2026-05-01': {
      title: 'Datenschutzerklärung & AGB aktualisiert',
      body: 'Sichtbarkeits­beschreibung an die neue Freigabe-Funktion für private Manuskripte angepasst und § 15 auf Review- und Freigabe-Einladungen unter denselben Schutzvorkehrungen erweitert (3 pro Manuskript, 14 Tage Gültigkeit, 1-Klick-Abmeldung, Sperrliste). Keine neuen Datenkategorien, keine neuen Empfänger, keine neuen Rechtsgrundlagen.',
    },
  },
  fr: {
    '2026-08-15': {
      title: 'Politique de confidentialité & CGU mises à jour',
      body: 'PaperVerse Writer est désormais couvert : nouveaux sous-traitants divulgués (Google Cloud Run dans l’UE/EEE pour la compilation LaTeX, OpenAlex aux États-Unis pour la recherche bibliographique — uniquement des termes de recherche, aucun contenu de manuscrit), le traitement par IA dans le Writer repose sur l\'exécution du contrat, et les CGU ajoutent les droits relatifs aux produits numériques (§§ 327 et s. BGB), des obligations d\'intégrité scientifique et une licence révocable clarifiée pour les brouillons privés. Références légales actualisées de la TMG vers la DDG.',
    },
    '2026-04-18': {
      title: 'Politique de confidentialité et cookies mise à jour',
      body: "Nous avons migré notre backend vers AWS Irlande. Vos données sont désormais stockées dans l'UE/EEE — aucun transfert vers un pays tiers pour le traitement principal.",
    },
    '2026-04-29': {
      title: 'Politique de confidentialité et CGU mises à jour',
      body: "Précisions dans la section sur les e-mails d'invitation à reviewer (DPF + Clauses contractuelles types pour Resend, divulgation des événements webhook, formulation de la durée de conservation) et mention de la limite déjà appliquée de 20 invitations par utilisateur sur 30 jours dans les CGU.",
    },
    '2026-04-30': {
      title: 'Pages juridiques — nettoyage rédactionnel',
      body: "Noms de tables internes retirés, versions de modèle généralisées, clause de langue contractuelle désormais en allemand et anglais (l'allemand prévaut), citation de l'Imprint mise à jour vers le § 18 (2) MStV, et préavis de modification des CGU aligné sur la norme de 30 jours. Aucun nouveau traitement.",
    },
    '2026-05-01': {
      title: 'Politique de confidentialité et CGU mises à jour',
      body: "Description de la visibilité adaptée à la nouvelle fonction de partage pour les manuscrits privés et § 15 élargi aux invitations à reviewer et de partage sous les mêmes garanties (3 par manuscrit, expiration à 14 jours, désinscription en 1 clic, liste de suppression). Aucune nouvelle catégorie de données, aucun nouveau destinataire, aucune nouvelle base juridique.",
    },
  },
  es: {
    '2026-08-15': {
      title: 'Política de privacidad y condiciones actualizadas',
      body: 'PaperVerse Writer ya está cubierto: nuevos encargados divulgados (Google Cloud Run en la UE/EEE para la compilación LaTeX, OpenAlex en EE. UU. para la búsqueda bibliográfica: solo términos de búsqueda, ningún contenido del manuscrito), el tratamiento por IA en el Writer se basa en la ejecución del contrato, y las condiciones añaden derechos sobre productos digitales (§§ 327 y ss. BGB), deberes de integridad científica y una licencia revocable aclarada para los borradores privados. Referencias legales actualizadas de TMG a DDG.',
    },
    '2026-04-18': {
      title: 'Política de Privacidad y Cookies actualizada',
      body: 'Hemos migrado nuestro backend a AWS Irlanda. Sus datos ahora se almacenan en la UE/EEE — sin transferencia a terceros países para el procesamiento principal.',
    },
    '2026-04-29': {
      title: 'Política de Privacidad y Condiciones actualizadas',
      body: 'Aclaraciones en la sección sobre correos de invitación de revisión (DPF + Cláusulas Contractuales Tipo para Resend, divulgación de eventos de webhook, redacción del plazo de conservación) e inclusión del límite ya aplicado de 20 invitaciones por usuario cada 30 días en las Condiciones.',
    },
    '2026-04-30': {
      title: 'Páginas legales — limpieza editorial',
      body: 'Nombres de tablas internas eliminados, versiones de modelo generalizadas, cláusula de idioma contractual ahora cubre alemán e inglés (prevalece la versión alemana), cita del Aviso Legal actualizada a § 18 (2) MStV y plazo de aviso de cambios en las Condiciones alineado al estándar de 30 días. Sin nuevo tratamiento.',
    },
    '2026-05-01': {
      title: 'Política de Privacidad y Condiciones actualizadas',
      body: 'Descripción de visibilidad adaptada a la nueva función de compartición para manuscritos privados y § 15 ampliado a invitaciones de revisión y de compartición bajo las mismas salvaguardas (3 por manuscrito, expiración de 14 días, desuscripción en 1 clic, lista de supresión). Sin nuevas categorías de datos, sin nuevos destinatarios, sin nuevas bases jurídicas.',
    },
  },
};

const STATIC_COPY: Record<SupportedLanguage, Pick<BannerCopy, 'whatChanged' | 'acknowledge' | 'dismiss'>> = {
  en: { whatChanged: 'What changed', acknowledge: 'Acknowledge', dismiss: 'Dismiss' },
  de: { whatChanged: 'Was sich geändert hat', acknowledge: 'Zur Kenntnis genommen', dismiss: 'Schließen' },
  fr: { whatChanged: 'Ce qui a changé', acknowledge: 'Pris en compte', dismiss: 'Fermer' },
  es: { whatChanged: 'Qué cambió', acknowledge: 'Entendido', dismiss: 'Cerrar' },
};

const GENERIC_TITLE: Record<SupportedLanguage, string> = {
  en: 'Privacy & Cookie Policy updated',
  de: 'Datenschutz- & Cookie-Richtlinie aktualisiert',
  fr: 'Politique de confidentialité et cookies mise à jour',
  es: 'Política de Privacidad y Cookies actualizada',
};

const genericBody = (language: SupportedLanguage, dateLabel: string): string => {
  switch (language) {
    case 'de': return `Unsere Datenschutz- und Cookie-Richtlinie wurde am ${dateLabel} aktualisiert.`;
    case 'fr': return `Notre politique de confidentialité et cookies a été mise à jour le ${dateLabel}.`;
    case 'es': return `Nuestra Política de Privacidad y Cookies se actualizó el ${dateLabel}.`;
    default: return `Our Privacy & Cookie Policy was updated on ${dateLabel}.`;
  }
};

const buildCopy = (language: SupportedLanguage): BannerCopy => {
  const version = DOCUMENT_VERSIONS.privacyPolicy;
  const versioned = VERSIONED_COPY[language]?.[version];
  const staticCopy = STATIC_COPY[language];
  if (versioned) {
    return { ...versioned, ...staticCopy };
  }
  // Fallback: generic, version-agnostic phrasing.
  return {
    title: GENERIC_TITLE[language],
    body: genericBody(language, formatVersionDate(version, language)),
    ...staticCopy,
  };
};

const readLanguage = (): SupportedLanguage => {
  try {
    const saved = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (saved && ['en', 'de', 'fr', 'es'].includes(saved)) {
      return saved as SupportedLanguage;
    }
  } catch {
    // ignore
  }
  return defaultLanguage;
};

/**
 * Non-blocking top banner that informs returning users when a legal-document
 * version has been bumped. Aggregates Privacy + Cookie policy acknowledgements
 * into a single notice. New users (no prior acknowledgement) do not see it.
 *
 * Banner copy reacts to runtime language switches via the `storage` event
 * (fired by other tabs) and a custom `paperverse:language-change` event
 * (which the LanguageSelector should dispatch on same-tab changes).
 *
 * Copy is selected via a versioned map (see VERSIONED_COPY above): if no entry
 * exists for the current `DOCUMENT_VERSIONS.privacyPolicy`, a generic
 * "updated on {date}" message is shown — so unrelated version bumps never
 * display stale narratives.
 */
export const PolicyUpdateBanner = () => {
  const privacy = useDocumentAcknowledgement('privacyPolicy');
  const cookie = useDocumentAcknowledgement('cookiePolicy');
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [language, setLanguage] = useState<SupportedLanguage>(() => readLanguage());

  useEffect(() => {
    const sync = () => setLanguage(readLanguage());
    const onStorage = (e: StorageEvent) => {
      if (e.key === LANGUAGE_COOKIE_NAME) sync();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('paperverse:language-change', sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('paperverse:language-change', sync);
    };
  }, []);

  const copy = buildCopy(language);
  const needsAnyAck = privacy.needsAck || cookie.needsAck;

  if (!needsAnyAck || dismissedThisSession) return null;

  // Dynamic link target: prefer the policy that needs acknowledgement, with
  // a version-specific anchor when one is known. Otherwise link to the page top.
  const version = DOCUMENT_VERSIONS.privacyPolicy;
  const hasVersionedCopy = !!VERSIONED_COPY[language]?.[version];
  const anchor = hasVersionedCopy ? `#changelog-${version}` : '';
  const linkTarget = privacy.needsAck
    ? `/privacy-policy${anchor}`
    : `/cookie-policy${anchor}`;

  const handleAcknowledge = async () => {
    await Promise.all([
      privacy.needsAck ? privacy.acknowledge() : Promise.resolve(),
      cookie.needsAck ? cookie.acknowledge() : Promise.resolve(),
    ]);
  };

  return (
    <div
      role="region"
      aria-label={copy.title}
      className="fixed top-0 inset-x-0 z-40 bg-primary text-primary-foreground shadow-md"
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1 text-sm">
          <p className="font-semibold">{copy.title}</p>
          <p className="opacity-90">
            {copy.body}{' '}
            <Link to={linkTarget} className="underline hover:opacity-100">
              {copy.whatChanged}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={handleAcknowledge}
          >
            {copy.acknowledge}
          </Button>
          <button
            type="button"
            onClick={() => setDismissedThisSession(true)}
            aria-label={copy.dismiss}
            className="p-1 hover:opacity-80 transition-opacity"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyUpdateBanner;
